import { useDictionaryContext } from 'application/contexts/DictionaryContext';
import { usePageContext } from 'application/contexts/PageContext';
import { useSiteContext } from 'application/contexts/SiteContext';
import { ChoosenFilter } from 'designsystem/components/2-molecules/ListFilters';
import { formatDate } from 'designsystem/utilities/date';
import { ElasticLang, getElasticIndexName } from 'features/Layouts/Layout/Vendors/Elastic/elasticHelpers';
import { getArticleGroupId } from 'helpers/getArticleGroupId';
import { UseElasticSearchProps, elasticSearchTagsName, elasticSearchTypesName, useElasticSearch } from 'hooks/useElasticSearch';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { CatalogPageFeature } from '../CatalogPage';

export const NewsCatalogPageAlternativeFeature: React.FC<Content.NewsCatalogPage> = ({
	properties,
	additionalTop,
	additionalPageContent,
	id,
	...rest
}) => {
	const { alphabeticSorting, filters: filterOptions, rootOverwrite } = properties ?? {};

	const [query, setQuery] = useState('*');
	const [dates, setDates] = useState<UseElasticSearchProps['dates']>(null);
	const [filters, setFilters] = useState<ChoosenFilter[]>(null);
	const [tags, setTags] = useState<UseElasticSearchProps['tags']>([]);
	const [types, setTypes] = useState<UseElasticSearchProps['types']>([]);
	const [preSelectedFilterTags, setPreSelectedFilterTags] = useState<UseElasticSearchProps['tags']>([]);
	const [preSelectedFilterTypes, setPreSelectedFilterTypes] = useState<UseElasticSearchProps['types']>([]);

	const dictionary = useDictionaryContext();
	const siteContext = useSiteContext();
	const pageContext = usePageContext();
	const router = useRouter();

	const { theme } = siteContext ?? {};

	const lang = router.locale.split('-')[0] as ElasticLang;

	const groupId = getArticleGroupId({ theme, id: rootOverwrite ? rootOverwrite.trim() : id, locale: lang });

	const { results, isLoading } = useElasticSearch({
		index: getElasticIndexName(theme, lang),
		parentId: id,
		tags: tags?.concat(preSelectedFilterTags),
		types: types?.concat(preSelectedFilterTypes),
		sort: alphabeticSorting ? 'title' : 'date',
		query: query,
		dates: dates,
		lang: lang,
	});

	useEffect(() => {
		const tagFilters = filterOptions?.items?.filter((node) => node?.content?.contentType === 'tagFolderPicker');
		const typeFilters = filterOptions?.items?.filter((node) => node?.content?.contentType === 'typesFolderPicker');

		const getPreSelectedFilters = (filterNodes): string[] => {
			if (!filterNodes) return [];
			const preFilters = filterNodes.filter((filter) => filter?.content?.properties?.preSelectedFilters);
			return preFilters
				.map((filter) => {
					const filterTags = filter?.content?.properties?.preSelectedFilters;
					return filterTags?.map((filter) => filter.name);
				})
				.flat();
		};

		const preSelectedTags = getPreSelectedFilters(tagFilters);
		const preSelectedTypes = getPreSelectedFilters(typeFilters);
		setPreSelectedFilterTags(preSelectedTags);
		setPreSelectedFilterTypes(preSelectedTypes);
	}, [filterOptions]);

	useEffect(() => {
		const tagFilters = filters?.filter((tag) => tag.groupId === elasticSearchTagsName)?.map((tag) => tag.value);
		const typeFilters = filters?.filter((type) => type.groupId === elasticSearchTypesName)?.map((type) => type.value);
		setTags(tagFilters);
		setTypes(typeFilters);
	}, [filters]);

	const handleSearch = (word) => {
		if (!word) return setQuery('*');
		setQuery(word);
	};

	const handleDateRange = (dates: Date[]) => {
		if (dates?.length !== 2 || !dates) return setDates(null);
		const [start, end] = dates ?? [];
		const from = start ? formatDate(String(start), 'yyyy-mm-dd') : '';
		const to = end ? formatDate(String(end), 'yyyy-mm-dd') : '';
		setDates({ from, to });
	};

	const rssFeedUrl = `/api/rss${pageContext.url}?group=${groupId.toLowerCase()}`;

	return (
		<CatalogPageFeature
			{...properties}
			{...rest}
			catalogListItems={!isLoading && (results as Models.CatalogListItem[])}
			additionalTop={additionalTop}
			additionalPageContent={additionalPageContent}
			noItemsMessage={dictionary.getServiceInfo('NoNews')}
			choosenDropdownFilters={(filters) => setFilters(filters)}
			searchWord={(word) => handleSearch(word)}
			dateRange={(dates) => handleDateRange(dates)}
			rssFeedUrl={rssFeedUrl}
		/>
	);
};
